import { useEffect } from "react";

/**
 * Lightweight per-route SEO helper — sets title, description, canonical,
 * OG/Twitter tags, and optional JSON-LD schema. No external deps.
 */
export default function useDocumentMeta({
  title,
  description,
  canonical,
  image,
  jsonLd, // array of objects or single object
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) =>
          el.setAttribute(k, v)
        );
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) =>
        el.setAttribute(k, v)
      );
      return el;
    };

    if (description) {
      setMeta('meta[name="description"]', {
        create: { name: "description" },
        set: { content: description },
      });
      setMeta('meta[property="og:description"]', {
        create: { property: "og:description" },
        set: { content: description },
      });
      setMeta('meta[name="twitter:description"]', {
        create: { name: "twitter:description" },
        set: { content: description },
      });
    }
    if (title) {
      setMeta('meta[property="og:title"]', {
        create: { property: "og:title" },
        set: { content: title },
      });
      setMeta('meta[name="twitter:title"]', {
        create: { name: "twitter:title" },
        set: { content: title },
      });
    }
    if (image) {
      setMeta('meta[property="og:image"]', {
        create: { property: "og:image" },
        set: { content: image },
      });
    }

    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    // Inject route-scoped JSON-LD (with marker so we can clean up)
    const scripts = [];
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      list.forEach((data) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.routeJsonld = "1";
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);
        scripts.push(s);
      });
    }

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);
}
